import React from 'react';
import axios from 'axios';
import {
  Alert, AlertIcon, Box, Button, Checkbox, FormControl, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, useToast,
} from '@chakra-ui/react';
import { FaFilePdf } from 'react-icons/fa';
import { useQueryClient } from 'react-query';
import useInvoice from '../hooks/useInvoice';
import useInvoices from '../hooks/useInvoices'; // item 1137 — billed-so-far vs the deal
import useProposals from '../hooks/useProposals'; // items 1138/1139/1142
import useCreditMemos from '../hooks/useCreditMemos'; // item 1221 — the apply-credit prompt
import {
  appliedCreditMap, proposalBilledMap, proposalDealAmount, proposalInvoiceDealMap,
} from '../models/pipeline';
import { ProposalStatus } from '../models/proposal';
import { CommissionReviewPanel } from './CommissionAdjustModal'; // item 341(b)
import { useConfirm } from './ConfirmProvider'; // item 1138 — the fully-billed close prompt
import openPdf from '../utils/openPdf'; // item 462c
import { money } from '../utils/money';

interface FinalizeInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  // item 434 — fires after a SUCCESSFUL finalize; item 462c — `send` is true when
  // the user chose FINALIZE & SEND here in the modal (the caller pops the send dialog)
  onFinalized?: (send: boolean) => void;
  // item 462c — only callers that can pop the send dialog show FINALIZE & SEND
  // (the invoice grid's modal can't — it hides the button)
  showSend?: boolean;
}

// item 38: confirm finalizing an invoice — it locks permanently (void-only after).
// item 341(b) — finalizing BOOKS the commissions, so the confirm shows the exact split
// one last time (still adjustable right here): the double-check backup Chad asked for.
const FinalizeInvoiceModal: React.FC<FinalizeInvoiceModalProps> = ({
  isOpen, onClose, invoiceId, onFinalized, showSend,
}) => {
  const { data, tryFinalize } = useInvoice({ id: invoiceId });
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  // items 1137/1138/1139/1142 — the proposal context for THIS invoice: what's actually
  // billed so far, what this finalize brings it to, and whether the deal is enterprise
  const { data: invList } = useInvoices();
  const { data: propList } = useProposals();
  const qc = useQueryClient();
  const confirmDialog = useConfirm();
  const invRow: any = (invList?.index ?? []).find((i: any) => i.id === invoiceId);
  const proposal: any = invRow?.proposalId
    ? (propList?.index ?? []).find((p: any) => p.id === invRow.proposalId)
    : undefined;
  const dealAmount = proposal
    ? proposalDealAmount(proposal,
      proposalInvoiceDealMap((invList?.index ?? []) as any).get(proposal.id) ?? 0)
    : 0; // item 1146 — invoice-derived truth for engine deals
  // item 1221 — applied credits count as BILLED (money the deal already collected), and the
  // customer's open memos drive the apply-credit prompt below
  const { memos, applications } = useCreditMemos();
  const billedSoFar = proposal
    ? (proposalBilledMap((invList?.index ?? []) as any, appliedCreditMap(applications)).get(proposal.id) ?? 0)
    : 0;
  const afterThis = billedSoFar + (invRow?.retailTotal ?? 0);
  const openMemos = (memos ?? []).filter(
    (m) => m.customerId === invRow?.customerId && m.remaining > 0,
  );
  // item 1355 (Chad: "I can only apply one credit to an invoice") — pick SEVERAL memos; they
  // apply in the order checked, each absorbing down to the remaining balance.
  const [applyMemoIds, setApplyMemoIds] = React.useState<string[]>([]);
  React.useEffect(() => { if (isOpen) setApplyMemoIds([]); }, [isOpen, invoiceId]);
  const toggleMemo = (id: string) => setApplyMemoIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const applyMemos = applyMemoIds.map((id) => openMemos.find((m) => m.id === id)).filter(Boolean) as typeof openMemos;
  const creditToApply = applyMemos.reduce((acc, m) => {
    const balance = Math.max(0, (invRow?.retailTotal ?? 0) - acc);
    return acc + Math.min(m.remaining, balance);
  }, 0);
  const memoIdsParam = applyMemoIds.length ? `CreditMemoIds=${applyMemoIds.join(',')}` : '';
  const overbills = Boolean(proposal) && dealAmount > 0 && afterThis > dealAmount + 0.005; // item 1139
  const fullyBills = Boolean(proposal) && dealAmount > 0 && afterThis >= dealAmount - 0.005; // item 1138
  const isEnterprise = Boolean(proposal?.isEnterprise); // item 1142
  // item 1140 — "book no commissions": set on the draft right before finalize; the accrual skips
  const [suppress, setSuppress] = React.useState(false);
  React.useEffect(() => { if (isOpen) setSuppress(false); }, [isOpen, invoiceId]);
  // item 733 (Chad) — the client PO is REQUIRED to finalize + printed on the invoice. Prompt for it
  // here (prefilled from the invoice / its proposal-WO chain) so finalize never dead-ends.
  const [po, setPo] = React.useState('');
  React.useEffect(() => { if (isOpen) setPo((data as any)?.customerPoNumber ?? ''); }, [isOpen, data]);
  // item 442/443 — progressive accrual: every commission-bearing invoice books its
  // cumulative slice at finalize; the copy names the share when it's partial
  const [booksNow, setBooksNow] = React.useState(true);
  const [shareLabel, setShareLabel] = React.useState<string | null>(null);
  // item 1140 — the button says exactly what will happen
  let finalizeLabel = booksNow ? 'Finalize — book commissions' : 'Finalize';
  if (suppress) finalizeLabel = 'Finalize — NO commissions';
  // item 759 (Chad) — the modal must be STATE-AWARE: reopened on an already-finalized invoice
  // it showed live rate inputs + Save split + Finalize, all guaranteed to bounce off the lock
  // ("what's the point of being able to update this page…"). Finalized → read-only review,
  // PO-only save, no Finalize buttons.
  // item 773 (Chad) — while OUR OWN finalize is in flight, the detail refetch flips
  // isFinalized true BEFORE the modal closes, flashing the orange already-finalized banners
  // for a few seconds. Suppress the state swap during the in-flight finalize; a genuine
  // reopen of a finalized invoice still gets the read-only view.
  const [finalizing, setFinalizing] = React.useState(false);
  // item 1206 (Chad: Tom's added split never booked) — finalize FLUSHES the panel's
  // unsaved edits first, so what the table shows is what books. No separate Save needed.
  const panelSaveRef = React.useRef<(() => Promise<boolean>) | null>(null);
  // item 948 (Chad) — you should never be ABLE to press Finalize when it can't succeed:
  // pre-flight the item-943 Performed-by rule here and disable the buttons with the reason.
  const linesMissingPerformer = ((data as any)?.invoiceItems ?? [])
    .filter((li: any) => !li.employeeId)
    .map((li: any) => li.sku || li.description || 'unnamed line');
  const finalized = Boolean((data as any)?.isFinalized) && !finalizing;

  const savePoOnly = async () => {
    if (!po.trim()) return;
    setBusy(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}invoice/${invoiceId}/customer-po`, JSON.stringify(po.trim()), {
        headers: { 'content-type': 'application/json' },
      });
      toast({ status: 'success', title: 'Client PO saved.' });
      onClose();
    } catch {
      toast({ status: 'error', title: 'Could not save the PO.' });
    } finally {
      setBusy(false);
    }
  };

  // item 1470 — finalizing an invoice whose flagged lines book NOBODY needs an explicit yes
  const guardNoCommissions = async (): Promise<boolean> => {
    if (booksNow || suppress) return true;
    return confirmDialog(
      'Every commission-flagged line on this invoice resolves to nobody (see the reasons above). Finalizing books NO commissions, permanently for this invoice. Fix the cause first, or finalize anyway?',
      { title: 'This will book NO commissions', confirmLabel: 'Finalize anyway', cancelLabel: 'Go back' },
    );
  };
  const onConfirm = async (send: boolean) => {
    if (!(await guardNoCommissions())) return;
    // item 733 (Chad) — the client PO is required before finalizing; save it first (works for a
    // draft AND is the same endpoint that edits it after finalize).
    if (!po.trim()) {
      toast({ status: 'error', title: 'Enter the client’s PO number', description: 'It’s required on the invoice before finalizing.' });
      return;
    }
    setBusy(true);
    setFinalizing(true); // item 773 — keep the draft view painted until we close
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      if (po.trim() !== ((data as any)?.customerPoNumber ?? '')) {
        await axios.post(`${apiUrl}invoice/${invoiceId}/customer-po`, JSON.stringify(po.trim()), {
          headers: { 'content-type': 'application/json' },
        });
      }
      // item 1206 — persist any unsaved panel edits (rate changes / added earners) so the
      // booking matches the table exactly; abort on failure rather than book the wrong split
      if (panelSaveRef.current && !(await panelSaveRef.current())) {
        setFinalizing(false);
        setBusy(false);
        return;
      }
      // item 1140 — flag the draft BEFORE finalize; the accrual reads it and books nothing
      if (suppress) {
        await axios.post(`${apiUrl}invoice/${invoiceId}/suppress-commissions`, { value: true });
      }
      // item 1221 — the chosen credit memo applies ATOMICALLY with the finalize (server-side:
      // negative no-commission line + memo decrement + lock, or none of it)
      await tryFinalize(invoiceId, memoIdsParam ? `apply${memoIdsParam}` : undefined);
      onClose();
      toast({
        status: 'success',
        title: suppress
          ? 'Invoice finalized — locked, NO commissions booked.'
          : 'Invoice finalized — it is now locked.',
        description: applyMemos.length
          ? `Credit applied: −${money(creditToApply)} (${applyMemos.map((m) => m.number).join(', ')}).`
          : undefined,
      });
      onFinalized?.(send);
      // item 1138 (Chad) — this finalize fully bills the proposal: offer to close it now.
      // Confirm, never silent — completion changes what the grid offers.
      if (fullyBills && proposal
        && (proposal.status === ProposalStatus.Approved
          || proposal.status === ProposalStatus.ApprovedDeposited)) {
        const close = await confirmDialog(
          `This invoice brings "${proposal.proposalTitle ?? proposal.proposalNumber ?? 'this proposal'}" to 100% billed (${money(afterThis)} of ${money(dealAmount)}). Mark the proposal Complete?`,
          { title: 'Proposal fully billed', confirmLabel: 'Mark Complete', cancelLabel: 'Leave open' },
        );
        if (close) {
          try {
            await axios.post(`${apiUrl}proposal/SetStatus/${proposal.id}`, ProposalStatus.Complete, {
              headers: { 'content-type': 'application/json' },
            });
            qc.invalidateQueries('proposals');
            qc.invalidateQueries('project');
            toast({ status: 'success', title: 'Proposal marked Complete.' });
            // item 1148 (Chad) — the edited-to-100% deposit leaves the WO open forever
            // (it only locks after a second invoice). Offer the one-click settle.
            const woId = invRow?.workOrderId;
            if (woId) {
              const settle = await confirmDialog(
                'The work order behind this proposal may still be open (no final invoice will ever generate). Close it too?',
                { title: 'Settle the work order', confirmLabel: 'Close work order', cancelLabel: 'Leave open' },
              );
              if (settle) {
                try {
                  // WorkOrderStatus.InvoicesGenerated = 3 — the terminal "fully invoiced" state
                  await axios.post(`${apiUrl}workorder/SetWoStatus/${woId}`, 3, {
                    headers: { 'content-type': 'application/json' },
                  });
                  qc.invalidateQueries('workOrders');
                  toast({ status: 'success', title: 'Work order closed.' });
                } catch (err2: any) {
                  toast({ status: 'warning', title: 'Could not close the work order', description: String(err2?.response?.data ?? 'Close it from the Work Orders grid.') });
                }
              }
            }
          } catch (err: any) {
            toast({ status: 'error', title: 'Could not complete the proposal', description: String(err?.response?.data ?? 'Mark it Complete from the proposals grid.') });
          }
        }
      }
    } catch (e: any) {
      // item 948 (Chad) — NEVER a generic failure: surface the server's actual reason
      // (missing Performed-by lines, locked project, …) so the fix is obvious.
      const serverMsg = e?.response?.data;
      // item 1294 (Chad hit a bare title mid-backend-restart) — the toast must ALWAYS say
      // WHY: server text when there is a response, otherwise name the transport failure
      // instead of silently dropping the reason.
      const description = (typeof serverMsg === 'string' && serverMsg.trim())
        ? serverMsg
        : (serverMsg?.error ?? serverMsg?.message ?? serverMsg?.title
          ?? (e?.response
            ? `The server rejected it (HTTP ${e.response.status}) without a reason — check the API log.`
            : 'No response from the server — it may be restarting or offline. Nothing was finalized; try again in a moment.'));
      toast({
        status: 'error',
        title: 'The invoice could not be finalized.',
        description,
        duration: 12000,
        isClosable: true,
      });
      setFinalizing(false); // a failed finalize returns to live state
    } finally {
      setBusy(false);
    }
  };

  // item 462c — preview the exact PDF from inside the confirm, before committing.
  // item 1293 — a SELECTED credit memo rides along so the preview shows the pending
  // credit line + net total (the real line only mints at finalize; without this the
  // preview contradicted the "finalizes at $X" note right above it).
  const preview = () => {
    openPdf(`invoice/${invoiceId}/pdf${memoIdsParam ? `?preview${memoIdsParam}` : ''}`).catch(() => toast({
      status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Finalize invoice</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={3}>
            {booksNow && shareLabel && `Finalizing locks this invoice permanently — its contents can no longer be
            edited. The commissions below are this invoice's ${shareLabel} of the deal —
            BOOKED when you finalize; the remainder books with later invoices. Adjust a
            rate here if something's off.`}
            {booksNow && !shareLabel && `Finalizing locks this invoice permanently — its contents can no longer be edited,
            and the commissions below are BOOKED exactly as shown. Adjust a rate here if
            something's off; after this it can only be voided (which reopens the work
            order so a corrected invoice can be generated).`}
            {!booksNow && `Finalizing locks this invoice permanently — its contents can no longer be
            edited; after this it can only be voided (which reopens the work order so a
            corrected invoice can be generated).`}
          </Text>
          {/* item 733 (Chad) — the client PO number, required + printed on the invoice */}
          <FormControl isRequired mb={4}>
            <FormLabel fontSize="sm">Client PO number</FormLabel>
            <Input
              value={po}
              onChange={(e) => setPo(e.target.value)}
              placeholder="The PO number the client issued for this work"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              Prints on the client&apos;s invoice. Editable later even after finalizing.
            </Text>
          </FormControl>
          {finalized && (
            <Text fontSize="sm" color="orange.300" mb={3}>
              This invoice is already finalized — its commissions are booked and locked. You can
              still update the client PO here; to correct a booked commission, use Reverse in the
              commissions ledger.
            </Text>
          )}
          {/* item 948 (Chad) — the blocker is VISIBLE before you can even try: name the
              lines missing "Performed by" and point at the fix. Buttons below are disabled. */}
          {!finalized && linesMissingPerformer.length > 0 && (
            <Alert status="warning" borderRadius="md" alignItems="flex-start" fontSize="sm" mb={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">
                  {`Can't finalize yet — ${linesMissingPerformer.length} line${linesMissingPerformer.length === 1 ? ' is' : 's are'} missing "Performed by".`}
                </Text>
                <Text mt={1}>
                  {`Set who performed the work on: ${linesMissingPerformer.slice(0, 6).join(' · ')}${linesMissingPerformer.length > 6 ? ' · …' : ''}`}
                </Text>
                <Text mt={1} color="gray.500">
                  Close this, open the Items section, and pick a name in the PERFORMED BY column.
                </Text>
              </Box>
            </Alert>
          )}
          {/* item 1137 — where this deal's billing actually stands, from real invoices */}
          {!finalized && proposal && dealAmount > 0 && (
            <Text fontSize="sm" color="gray.500" mb={3}>
              {`Proposal ${proposal.proposalNumber ?? ''}: ${money(billedSoFar)} of ${money(dealAmount)} billed so far — this invoice brings it to ${Math.round((afterThis / dealAmount) * 100)}%.`}
            </Text>
          )}
          {/* item 1139 (Chad) — the overbill catcher: a deposit edited to bill more than its
              split makes the auto-computed remainder a double-bill. Loud, before the lock. */}
          {!finalized && overbills && (
            <Alert status="error" borderRadius="md" alignItems="flex-start" fontSize="sm" mb={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">
                  {`This would bill ${Math.round((afterThis / dealAmount) * 100)}% of the proposal (${money(afterThis)} of ${money(dealAmount)}).`}
                </Text>
                <Text mt={1}>
                  An earlier invoice likely already covered part of this amount (e.g. a deposit
                  edited to bill in full). Double-check before finalizing — this locks.
                </Text>
              </Box>
            </Alert>
          )}
          {/* item 1142 (Chad) — ENTERPRISE deal reminder: team-effort comp. Reminder + the
              panel below to zero rates — never automatic. */}
          {!finalized && isEnterprise && (
            <Alert status="info" borderRadius="md" alignItems="flex-start" fontSize="sm" mb={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">ENTERPRISE deploy — team-effort compensation.</Text>
                <Text mt={1}>
                  Commissions other than IO Sales / Sales PO are typically zeroed on this deal
                  (discretionary bonuses replace them if the project lands profitable). Sales
                  reps keep standard pay. Zero the other rates in the panel below before booking.
                </Text>
              </Box>
            </Alert>
          )}
          {/* item 1221 (Chad's flow) — the customer has unapplied credit: offer to apply it
              WITH the finalize. Yes → a negative no-commission "Credit" line lands and the
              invoice locks at the honest net; No → the credit stays banked for next time. */}
          {!finalized && openMemos.length > 0 && (
            <Alert status="info" borderRadius="md" alignItems="flex-start" fontSize="sm" mb={4}>
              <AlertIcon />
              <Box w="100%">
                <Text fontWeight="semibold">
                  {`This customer has ${money(openMemos.reduce((s, m) => s + m.remaining, 0))} in unapplied credit.`}
                </Text>
                <Text mt={1}>
                  Check the credit memos to apply now — each lands as a credit line and the invoice
                  finalizes at the net amount. Leave them unchecked and the credit stays on the account.
                </Text>
                <HStack mt={2} spacing={3}>
                  <Button size="xs" variant="outline" onClick={() => setApplyMemoIds(openMemos.map((m) => m.id))} isDisabled={applyMemoIds.length === openMemos.length}>
                    Apply all
                  </Button>
                  <Button size="xs" variant="ghost" onClick={() => setApplyMemoIds([])} isDisabled={applyMemoIds.length === 0}>
                    Don&apos;t apply credit
                  </Button>
                </HStack>
                <Stack mt={2} spacing={1}>
                  {openMemos.map((m) => (
                    <Checkbox key={m.id} size="sm" isChecked={applyMemoIds.includes(m.id)} onChange={() => toggleMemo(m.id)}>
                      {`${m.number} — ${money(m.remaining)} available${m.note ? ` · ${m.note.slice(0, 60)}` : ''}`}
                    </Checkbox>
                  ))}
                </Stack>
                {applyMemos.length > 0 && (
                  <Text mt={2} fontWeight="semibold">
                    {`Applies −${money(creditToApply)} (${applyMemos.length} memo${applyMemos.length === 1 ? '' : 's'}) → this invoice finalizes at ${money(Math.max(0, (invRow?.retailTotal ?? 0) - creditToApply))}.`}
                  </Text>
                )}
              </Box>
            </Alert>
          )}
          {isOpen && (
            <CommissionReviewPanel
              invoiceId={invoiceId}
              readOnly={finalized}
              saveRef={panelSaveRef}
              // item 1373 (Chad: "save split should not be required as we discussed") — the
              // finalize flush persists every edit, so the panel's own Save split button is
              // redundant here and only suggested a step order that lost the added earner.
              showSave={false}
              onBooksOnFinalize={(books, label) => { setBooksNow(books); setShareLabel(label ?? null); }}
            />
          )}
          {/* item 1140 (Chad) — legacy/migrated billing: book NOTHING, explicitly */}
          {!finalized && (
            <Checkbox mt={4} isChecked={suppress} onChange={(e) => setSuppress(e.target.checked)}>
              <Text as="span" fontSize="sm" fontWeight="semibold">Book NO commissions on this invoice</Text>
              <Text fontSize="xs" color="gray.500">
                For migrated/legacy billing whose commissions were settled outside Basix. Nothing
                accrues for anyone — explicit and permanent once finalized.
              </Text>
            </Checkbox>
          )}
        </ModalBody>
        <ModalFooter>
          {/* item 462c — see exactly what the customer would get, right here */}
          <Button variant="ghost" leftIcon={<FaFilePdf />} mr="auto" onClick={preview}>
            Preview PDF
          </Button>
          <Button variant="ghost" mr={3} onClick={onClose}>{finalized ? 'Close' : 'Cancel'}</Button>
          {finalized ? (
            <Button onClick={savePoOnly} isLoading={busy}>Save PO</Button>
          ) : (
            <Button
              variant={showSend ? 'outline' : 'solid'}
              mr={showSend ? 3 : 0}
              onClick={() => onConfirm(false)}
              isLoading={busy}
              isDisabled={linesMissingPerformer.length > 0}
              title={linesMissingPerformer.length > 0 ? 'Every line needs "Performed by" first' : undefined}
            >
              {finalizeLabel}
            </Button>
          )}
          {showSend && !finalized && (
            <Button
              onClick={() => onConfirm(true)}
              isLoading={busy}
              isDisabled={linesMissingPerformer.length > 0}
              title={linesMissingPerformer.length > 0 ? 'Every line needs "Performed by" first' : undefined}
            >
              Finalize &amp; send
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

FinalizeInvoiceModal.defaultProps = { onFinalized: undefined, showSend: false };

export default FinalizeInvoiceModal;
